/**
 * documentFieldConfig.ts — silnik formatek dokumentów (E4, decyzja 4.1.b, §2.2).
 *
 * Jawna mapa per-slug dla 13 pól core dokumentów klas gołych (commercial /
 * internal): slug seedowy (snake_case) → klucz stanu w BareDocFormData
 * (camelCase) + rodzaj nadpisania widgetu + wskazówki prezentacji
 * (colSpan / maxLength / rows). Jedna struktura scala mapowanie stanu
 * (§4.1.5) z nadpisaniami (§4.1.6).
 *
 * Mapa jest JAWNA (nie generowana snake→camel), żeby była odporna na
 * przyszły slug spoza wzorca i samodokumentująca.
 */

import type { BareDocFormData } from '@/components/documents/documentCoreRenderers';

export type DocFieldOverride =
  | 'systemNumber' // TextInput disabled=true zawsze (niezależnie od editable)
  | 'status' // StatusSelect (enum hardkodowany)
  | 'urgency' // UrgencySelect
  | 'source' // SourceSelect
  | 'ownerUserId' // UserPicker
  | 'category' // RecordCategoryPicker (documentCategoryRenderer.tsx) — wspólna dla wszystkich klas dokumentów
  | 'documentTypeId'; // strukturalne: NIE renderowane inline (badge w nagłówku, create-only) → renderField zwraca null

export interface DocFieldConfig {
  /** klucz w BareDocFormData (camelCase). */
  stateKey: keyof BareDocFormData;
  /** jeśli obecny → renderuj nadpisanie zamiast ExtFieldValueInput. */
  override?: DocFieldOverride;
  /** placement — trafia na <Field colSpan>. Domyślnie 1. */
  colSpan?: 1 | 2;
  /** limit dla varchar/text (przekazany do ExtFieldValueInput jako maxLength). */
  maxLength?: number;
  /** rows dla widgetu 'text' (Textarea). */
  rows?: number;
  /**
   * Ground-truth `fieldType` z seeda (`apps/api/lib/seed-system-modules.ts`,
   * `commercial`/`internal` coreFields) — używany przez fallback statyczny
   * `useDocumentRenderField` (403 na GET .../fields, ADR-0071) do zbudowania
   * `EngineFormField[]` bez zapytania sieciowego. `document_type_id` NIE jest
   * seedowany jako coreField (strukturalny, poza layoutem) — 'reference' jest
   * wartością zaślepką, bo ten slug i tak renderuje null (override: 'documentTypeId').
   */
  fieldType: string;
}

/** Klucz = slug seedowy (snake_case). Kolejność referencyjna EMPTY_BARE_DOC_FORM. */
export const DOCUMENT_FIELD_CONFIG: Record<string, DocFieldConfig> = {
  system_number: { stateKey: 'systemNumber', override: 'systemNumber', fieldType: 'varchar' },
  title: { stateKey: 'title', colSpan: 2, maxLength: 500, fieldType: 'varchar' },
  status: { stateKey: 'status', override: 'status', fieldType: 'select' },
  urgency: { stateKey: 'urgency', override: 'urgency', fieldType: 'select' },
  document_date: { stateKey: 'documentDate', fieldType: 'date' }, // ExtFieldValueInput 'date' branch — zgodny natywnie
  owner_user_id: { stateKey: 'ownerUserId', override: 'ownerUserId', fieldType: 'reference' },
  category_id: { stateKey: 'categoryId', override: 'category', fieldType: 'select' },
  description: { stateKey: 'description', colSpan: 2, maxLength: 1000, fieldType: 'text' },
  notes: { stateKey: 'notes', colSpan: 2, rows: 3, fieldType: 'text' }, // fieldType 'text'
  rejection_reason: {
    stateKey: 'rejectionReason',
    colSpan: 2,
    rows: 2,
    fieldType: 'text',
  }, // fieldType 'text'
  source: { stateKey: 'source', override: 'source', fieldType: 'select' },
  external_id: { stateKey: 'externalId', maxLength: 100, fieldType: 'varchar' },
  external_source: { stateKey: 'externalSource', maxLength: 100, fieldType: 'varchar' },
  document_type_id: {
    stateKey: 'documentTypeId',
    override: 'documentTypeId',
    fieldType: 'reference',
  },
};
